import type { CategoryName } from "@/stores/fileImport"
import type { CellCoord, UploadFile } from "@/types"
import type { WasmApi } from "@kaleidoscope-solutions/engine-wasm"
import Fuse from "fuse.js"

const NO_COLUMN_INDEX = -1

type ColumnIndex = number
type DatasetName = string
type LocateTarget = CategoryName | DatasetName

enum Strategy {
  Exact,
  Fuzzy,
  ExternalNames,
}

// Auto-located candidates are added to this map during the auto-locate process.
type CandidateMap = Map<LocateTarget, StrategyResult[]>

// The final auto-locate result for all targets which had a valid, non-ambiguous candidate column.
type LocatedMap = Map<LocateTarget, ColumnIndex>

// Return type from a call to a locate strategy.
//
class StrategyResult {
  strategy: Strategy
  candidateColumnIndexes: ColumnIndex[]

  constructor(strategy: Strategy, candidateColumnIndexes: ColumnIndex[]) {
    this.strategy = strategy
    this.candidateColumnIndexes = candidateColumnIndexes
  }
}

// Return a map of categories (or the dataset name - for the value column) which have been auto-located to column
// indexes in the file based on various name matching strategies. Only un-ambiguous results are returned.
//
export function autoLocateColumnHeaders(
  modelApi: WasmApi,
  file: UploadFile,
  targetNames: LocateTarget[],
  targetCategories: string[]
): LocatedMap {
  const columnHeaders = file.scraped?.headers ?? []
  const candidateMap: CandidateMap = new Map<string, StrategyResult[]>()

  // eslint-disable-next-line no-console
  console.info("Auto locating columns")

  const allTargets = [...targetNames, ...targetCategories]

  allTargets.forEach((targetName) => {
    const result = autoLocateExact(targetName, columnHeaders)
    mergeResultWithCandidateMap(targetName, result, candidateMap, columnHeaders)
  })

  // Exclude any potential exact matches from the remaining targets to locate.
  // These will either take precedence (single exact match) or be excluded as ambiguous targets (multiple columns match exactly).
  const targetsForFuzzy = allTargets.filter(
    (targetName) => !candidateMap.has(targetName)
  )

  // Fuzzy matching is currently disabled.
  // targetsForFuzzy.forEach((targetName) => {
  //   const result = autoLocateFuzzy(targetName, columnHeaders)
  //   mergeResultWithCandidateMap(targetName, result, candidateMap, columnHeaders)
  // })

  // Exclude the dataset value from the external names' strategy.
  const categoryOnlyTargets = targetsForFuzzy.filter(
    (categoryTarget) => !targetNames.includes(categoryTarget)
  )

  categoryOnlyTargets.forEach(async (targetName) => {
    const externalNameMap = <Map<string, string>>(
      await modelApi.externalNameMap(targetName)
    )

    const result = await autoLocateViaExternalNames(
      externalNameMap,
      targetName,
      file
    )
    mergeResultWithCandidateMap(targetName, result, candidateMap, columnHeaders)
  })

  const resultMap = extractUnambiguousCandidates(candidateMap)

  // eslint-disable-next-line no-console
  console.info("Auto locating complete")

  return resultMap
}

// Take non-ambiguous candidates out of the candidate map and return them in the LocatedMap.
//
function extractUnambiguousCandidates(candidateMap: CandidateMap): LocatedMap {
  const locatedMap = new Map<string, number>()

  // Accept any targets with non-ambiguous exact matches.
  evaluateAndTakeCandidates(candidateMap, locatedMap, exactStrategyFilter())

  // Black-list columns which are suggested for more than one target.
  const ambiguousColumns: Set<ColumnIndex> =
    findColumnsUsedByMultipleTargets(candidateMap)

  // Accept non-ambiguous candidates.
  evaluateAndTakeCandidates(
    candidateMap,
    locatedMap,
    nonAmbiguousFilter(ambiguousColumns)
  )

  return locatedMap
}

// If there are candidate column indexes in the result, add the result to the candidate map for the target.
//
function mergeResultWithCandidateMap(
  targetName: LocateTarget,
  result: StrategyResult,
  candidateMap: CandidateMap,
  columnHeaders: string[]
) {
  if (result.candidateColumnIndexes.length === 0) {
    return
  }

  const strategyName = Strategy[result.strategy]

  const formattedColumns = formatColumnHeadersWithIndexes(
    result.candidateColumnIndexes,
    columnHeaders
  )

  // eslint-disable-next-line no-console
  console.info(
    `Column(s) [${formattedColumns}] are candidates for ${targetName} using ${strategyName} strategy`
  )

  const existingCandidateResults = candidateMap.get(targetName) || []

  existingCandidateResults.push(result)
  candidateMap.set(targetName, existingCandidateResults)
}

// Apply a filter to the candidate map.
//
// All entries matching the filter are removed from the candidate map.
//
// Any entries matching the filter with a single unique candidate column (across all strategy results) are added to the located map.
//
function evaluateAndTakeCandidates(
  candidateMap: CandidateMap,
  locatedMap: LocatedMap,
  candidateFilter: CandidateFilter
) {
  const filteredCandidates = [...candidateMap].filter(candidateFilter)

  filteredCandidates.forEach(([targetName, results]) => {
    const uniqueColumnIndexes = getUniqueColumnIndexes(results)

    // If there is a sole unique column create a candidate from it.
    if (uniqueColumnIndexes.length === 1) {
      // Create a candidate for it.
      locatedMap.set(targetName, uniqueColumnIndexes[0])
    } else {
      // eslint-disable-next-line no-console
      console.info(
        `Multiple columns suggested for ${targetName} therefore they are ambiguous: ${uniqueColumnIndexes}`
      )
    }

    // Remove the candidate from the candidate map.
    candidateMap.delete(targetName)
  })
}

// Return a unique set of candidate column indexes from all of the strategy results provided.
//
function getUniqueColumnIndexes(results: StrategyResult[]): ColumnIndex[] {
  const columnIndexes: Set<ColumnIndex> = new Set()
  results.forEach((result) =>
    result.candidateColumnIndexes.forEach((columnIndex) =>
      columnIndexes.add(columnIndex)
    )
  )
  return [...columnIndexes]
}

// Return all candidate column indexes in the candidate map which have been suggested for more than one target.
//
function findColumnsUsedByMultipleTargets(
  candidateMap: CandidateMap
): Set<ColumnIndex> {
  let allUsedColumnIndexes: ColumnIndex[] = []

  // Get the unique candidate column indexes across all strategy results for a given target. Add them to the 'main' array.
  Array.from(candidateMap.values()).forEach(
    (results) =>
      (allUsedColumnIndexes = [
        ...allUsedColumnIndexes,
        ...getUniqueColumnIndexes(results),
      ])
  )

  // Now count the occurrences of each column index in the 'main' array.
  const counts = new Map<ColumnIndex, number>()

  for (const columnIndex of allUsedColumnIndexes) {
    counts.set(
      columnIndex,
      counts.has(columnIndex) ? counts.get(columnIndex)! + 1 : 1
    )
  }

  const ambiguousColumns = [...counts]
    .filter(([_columnIndex, count]) => count > 1)
    .map(([columnIndex, _count]) => columnIndex)

  if (ambiguousColumns.length > 0) {
    // eslint-disable-next-line no-console
    console.info(
      `These columns were suggested for multiple targets and are ambiguous: ${ambiguousColumns}`
    )
  }

  return new Set(ambiguousColumns)
}

type CandidateFilter = (
  candidate: [targetName: LocateTarget, strategyResults: StrategyResult[]]
) => boolean

function exactStrategyFilter(): CandidateFilter {
  return ([_targetName, strategyResults]) =>
    strategyResults.some(
      (strategyResult) => strategyResult.strategy === Strategy.Exact
    )
}

function nonAmbiguousFilter(
  ambiguousColumns: Set<ColumnIndex>
): CandidateFilter {
  return ([_targetName, strategyResults]) =>
    !strategyResults.some((result) =>
      hasAmbiguousColumns(result, ambiguousColumns)
    )
}

function hasAmbiguousColumns(
  result: StrategyResult,
  ambiguousColumns: Set<ColumnIndex>
): boolean {
  return result.candidateColumnIndexes.some((candidateColumnIndex) =>
    ambiguousColumns.has(candidateColumnIndex)
  )
}

function formatColumnHeadersWithIndexes(
  columnIndexes: ColumnIndex[],
  columnHeaders: string[]
): string {
  return columnIndexes
    .map((columnIndex) => `${columnIndex}: ${columnHeaders[columnIndex]}`)
    .join(", ")
}

// Compare using the default locale.
function compareStringCaseInsensitive(string_1: string, string_2: string) {
  return (
    string_1.localeCompare(string_2, undefined, { sensitivity: "accent" }) === 0
  )
}

// Return all column headers which exactly match the target name (case in-sensitive).
//
function autoLocateExact(
  targetName: LocateTarget,
  columnHeaders: string[]
): StrategyResult {
  const candidateColumnIndexes = columnHeaders
    .map((header, index) =>
      compareStringCaseInsensitive(header, targetName) ? index : NO_COLUMN_INDEX
    )
    .filter((index) => index !== NO_COLUMN_INDEX)

  return new StrategyResult(Strategy.Exact, candidateColumnIndexes)
}

// Return all column headers which fuzzy match the target name.
//
export function autoLocateFuzzy(
  targetName: LocateTarget,
  columnHeaders: string[]
): StrategyResult {
  const options = {
    isCaseSensitive: false,

    // Scores range from 0 = perfect match to 1 = mismatch
    // This threshold is a guess. I've seen 'thing 3' match with 'Months' with a score of ~0.69
    threshold: 0.2,

    // Useful for debugging
    includeScore: false,
    includeMatches: false,
    shouldSort: false,
  }

  const fuse = new Fuse(columnHeaders, options)

  const candidateColumnIndexes = fuse
    .search(targetName)
    .map((result) => result.refIndex)

  return new StrategyResult(Strategy.Fuzzy, candidateColumnIndexes)
}

// Return all column headers which have one or more row items intersecting the external names of the category.
//
// This is not efficient for large files / maps.
//
function autoLocateViaExternalNames(
  externalNameMap: Map<string, string>,
  categoryName: CategoryName,
  file: UploadFile
): StrategyResult {
  if (file.scraped === undefined) {
    return new StrategyResult(Strategy.ExternalNames, [])
  }

  const candidateColumnIndexes: ColumnIndex[] = []

  for (
    let columnIndex = 0;
    columnIndex < file.scraped.headers.length;
    columnIndex++
  ) {
    for (let rowIndex = 0; rowIndex < file.scraped.rows.length; rowIndex++) {
      const cellText = file.scraped.rows[rowIndex][columnIndex]

      if (externalNameMap.has(cellText)) {
        candidateColumnIndexes.push(columnIndex)
        break
      }
    }
  }

  return new StrategyResult(Strategy.ExternalNames, candidateColumnIndexes)
}

export function cellCoord(row: number, col: number): CellCoord {
  return `${row},${col}`
}
